import React from "react";
import {
  Plane,
  ShieldCheck,
  Mail,
  CheckCircle2,
  Clock,
} from "lucide-react";

// ── Utilitaires de formatage (n'affiche que des données réelles) ───────────

// "PT26H35M" → "26 h 35 min"
function formatDuration(iso) {
  if (!iso) return "";
  const h = (iso.match(/(\d+)H/) || [])[1];
  const m = (iso.match(/(\d+)M/) || [])[1];
  const parts = [];
  if (h) parts.push(`${h} h`);
  if (m) parts.push(`${m} min`);
  return parts.join(" ") || iso;
}

// "2025-09-19T09:10:00" → "19/09/2025 • 09:10"
function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} • ${time}`;
}

function formatPrice(amount, currency = "XOF") {
  const n = Number(amount);
  if (Number.isNaN(n) || n === 0) return "—";
  return `${new Intl.NumberFormat("fr-FR").format(Math.round(n))} ${currency}`;
}

const TYPE_LABELS = { ADULT: "Adulte", CHILD: "Enfant", HELD_INFANT: "Bébé" };

const ReservationReceipt = ({ data }) => {
  if (!data) {
    return (
      <div className="p-6 text-center text-gray-500">
        Aucune donnée de réservation disponible.
      </div>
    );
  }

  const flights = data.flight_data || [];
  const passengers = data.passenger_data || [];

  // ── PAIEMENT ──
  const payment = data.payment || {};
  const paymentStatus =
    payment.status || (data.status === "success" ? "success" : "pending");
  const payed = paymentStatus === "success";
  const amountPayé =
    payment.amount != null
      ? payment.amount
      : flights[0]?.adjustedPrice ?? flights[0]?.price?.total ?? null;
  const currency = payment.currency || "XOF";
  const reference = data.reference || data.booking?.bookingId || "";
  const provider = payment.provider || "paystack";

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* ════════════ STATUT PAIEMENT ════════════ */}
      <div
        className={`rounded-2xl p-6 text-white shadow-lg mb-8 ${
          payed
            ? "bg-gradient-to-r from-emerald-500 to-emerald-600"
            : "bg-gradient-to-r from-amber-500 to-orange-500"
        }`}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              {payed ? (
                <CheckCircle2 className="w-8 h-8" />
              ) : (
                <Clock className="w-8 h-8" />
              )}
            </div>
            <div>
              <p className="text-xl font-bold">
                {payed ? "Paiement confirmé" : "Paiement en attente"}
              </p>
              <p className="text-white/85 text-sm">
                {payed
                  ? "Votre réservation est enregistrée et confirmée."
                  : "Votre réservation est enregistrée, la vérification du paiement est en cours."}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-extrabold">
              {formatPrice(amountPayé, currency)}
            </p>
            <p className="text-white/85 text-sm uppercase tracking-wide">
              {provider} • {payed ? "Validé" : "En attente"}
            </p>
          </div>
        </div>
      </div>

      {/* ════════════ RÉFÉRENCE / CONTACT ════════════ */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
              Référence de réservation
            </p>
            <p className="text-lg font-bold font-mono">{reference || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
              Email de contact
            </p>
            <p className="text-lg font-semibold flex items-center gap-2 break-all">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              {data.email || "—"}
            </p>
          </div>
        </div>
      </div>

      {/* ════════════ VOL(S) ════════════ */}
      {flights.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center text-gray-500 mb-6">
          Aucune donnée de vol disponible.
        </div>
      ) : (
        <div className="space-y-6 mb-6">
          {flights.map((flight, idx) => {
            const itineraries = flight.itineraries || [];
            const airlineCode =
              flight.validatingAirlineCodes?.[0] ||
              itineraries[0]?.segments?.[0]?.carrierCode ||
              "";
            const firstLeg = itineraries[0]?.segments?.[0];
            const lastLeg = itineraries.at(-1)?.segments?.at(-1);

            return (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {firstLeg?.departure?.iataCode || "???"} ⟶{" "}
                      {lastLeg?.arrival?.iataCode || "???"}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {itineraries.length === 2
                        ? "Aller / Retour"
                        : itineraries.length > 1
                        ? `${itineraries.length} trajets`
                        : "Aller simple"}
                      {airlineCode ? ` • ${airlineCode}` : ""}
                    </p>
                  </div>
                </div>

                {itineraries.map((it, i) => {
                  if (!it.segments || it.segments.length === 0) return null;
                  const first = it.segments[0];
                  const last = it.segments[it.segments.length - 1];
                  return (
                    <div key={i} className="mb-4 last:mb-0">
                      {itineraries.length > 1 && (
                        <p className="text-xs font-semibold text-blue-600 mb-2">
                          {i === 0 ? "Aller" : "Retour"}
                        </p>
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-4 bg-gray-50 rounded-xl p-4">
                        <div>
                          <p className="text-xl font-bold text-gray-800">
                            {first.departure?.iataCode || "?"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(first.departure?.at)}
                          </p>
                        </div>
                        <div className="text-center flex-1 min-w-[120px]">
                          <p className="text-xs text-gray-500 mb-1">
                            {formatDuration(it.duration)}
                          </p>
                          <div className="flex items-center gap-1">
                            <span className="h-px flex-1 bg-gray-300" />
                            <Plane className="w-4 h-4 text-blue-500" />
                            <span className="h-px flex-1 bg-gray-300" />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {first.carrierCode}
                            {first.number
                              ? ` · Vol ${first.carrierCode}${first.number}`
                              : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-800">
                            {last.arrival?.iataCode || "?"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(last.arrival?.at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ════════════ PASSAGERS ════════════ */}
      {passengers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Passagers ({passengers.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {passengers.map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {p.name?.firstName?.charAt(0) || "?"}
                </div>
                <div>
                  <p className="font-semibold text-gray-800">
                    {[p.name?.firstName, p.name?.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {TYPE_LABELS[p.travelerType] || p.travelerType || "Passager"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ════════════ NOTE ════════════ */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start gap-3">
        <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800">
          Un email de confirmation{" "}
          {data.email_sent ? "a été envoyé" : "vous sera envoyé"} à votre
          adresse. Veuillez présenter ce reçu lors de l’enregistrement.
        </p>
      </div>
    </div>
  );
};

export default ReservationReceipt;